import { Float } from '@headlessui-float/react'
import { Menu, MenuButton, MenuItems } from '@headlessui/react'
import React from 'react'
import { useTranslation } from 'next-i18next'
import { LocaleEnum, localeNames } from 'shared/enums/locale-enum'
import { useFloatHover } from 'shared/hooks/use-float-hover'
import { getCalculatedLanguage } from 'shared/utils/get-calculated-language'
import useUser from '../hooks/use-user'
import ChevronDownIcon from '../icons/chevron-down-icon'

type LanguageSwitcherProps = {
  changeLanguage: (locale: LocaleEnum) => () => void
}

export function PublicDashboardLanguageSwitcher({ changeLanguage }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const { user } = useUser()
  const { show, delayClose, delayOpen } = useFloatHover()
  if (!i18n) {
    return <div>loading...</div>
  }

  return !user ? (
    <div className="h-6 w-[44px] animate-pulse rounded-sm bg-gray" />
  ) : (
    <Float
      show={show}
      placement="bottom"
      offset={-5}
      arrow
      enter="transition duration-100 ease-out"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition duration-75 ease-in"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      className={'flex h-full items-center'}
    >
      <button
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
        className="flex h-full items-center justify-around gap-2.5 border-b-4 border-transparent focus:outline-none focus-visible:border-blue"
      >
        <div className="w-5 text-sm font-bold text-white/90">
          {i18n && i18n.language ? getCalculatedLanguage(i18n.language) : ''}
        </div>
        <ChevronDownIcon />
      </button>

      <div
        onMouseEnter={delayOpen}
        onMouseLeave={delayClose}
        className="flex flex-col rounded-lg bg-white py-4 text-sm shadow-lg"
      >
        <Float.Arrow
          className={`absolute left-0 size-0 -translate-y-3.5 border-[10px] border-x-transparent border-b-white border-t-transparent`}
        />
        <ul>
          {Object.keys(localeNames)
            .filter(dashboardLocale => dashboardLocale !== i18n.language)
            .map(dashboardLocale => (
              <li key={dashboardLocale}>
                <button className="w-full cursor-pointer px-7 py-1 text-start text-gray-300 hover:bg-lightblue focus:outline-none focus-visible:text-blue">
                  <p onClick={changeLanguage(dashboardLocale as LocaleEnum)}>
                    {localeNames[dashboardLocale as LocaleEnum]}
                  </p>
                </button>
              </li>
            ))}
        </ul>
      </div>
    </Float>
  )
}

export function MobilePublicLanguageSwitcher({ changeLanguage }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const { user } = useUser()

  if (!i18n) {
    return <div>loading...</div>
  }

  return (
    <Menu>
      <Float
        as="div"
        className="relative"
        placement="top-end"
        flip
        offset={-25}
        enter="transition duration-300 ease-out"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition duration-175 ease-in"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <MenuButton className="relative flex items-center justify-between gap-2 px-4 py-2">
          <span className="flex-1 text-start capitalize text-bluegray">
            {user?.dashboardLocale}
          </span>
          <ChevronDownIcon className="size-3 rotate-180" aria-hidden="true" />
        </MenuButton>

        <MenuItems
          className="divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
          static
        >
          <div className="py-1">
            {Object.keys(localeNames)
              .filter(dashboardLocale => dashboardLocale !== i18n.language)
              .map(dashboardLocale => (
                <MenuButton
                  className="my-2 px-4 py-1 text-bluegray"
                  onClick={changeLanguage(dashboardLocale as LocaleEnum)}
                  key={dashboardLocale}
                >
                  {localeNames[dashboardLocale as LocaleEnum]}
                </MenuButton>
              ))}
            {Object.keys(localeNames).includes(i18n.language) && (
              <MenuButton
                className="my-2 flex w-full items-center px-4 py-1 text-blue"
                onClick={changeLanguage(i18n.language as LocaleEnum)}
              >
                {localeNames[i18n.language as LocaleEnum]}
                <ChevronDownIcon className="ml-3 size-3 rotate-180" aria-hidden="true" />
              </MenuButton>
            )}
          </div>
        </MenuItems>
      </Float>
    </Menu>
  )
}
